import { useQuery } from '@tanstack/react-query';

import { getTwentileCountsByMailing, type TwentileCountsByMailing } from '@enterprise/api';
import { ensureError, isDefined, isNotDefined } from '@enterprise/core';
import type { OperationResultWithData } from '@enterprise/types';

import type { NthCalculatorRow } from '@components/nth-calculator/types/nth-calculator-row.type';

import { useApplicationStore } from '@hooks/application/application-store.hook';
import { nthQueryKeys } from '@components/nth-calculator/common/nth-query-keys';

import type { UseGetTwentileCountsByMailingResult } from './types/get-twentile-counts-by-mailing.type';

export const useGetTwentileCountsByMailing = (mailingId?: number): UseGetTwentileCountsByMailingResult => {
  const enterpriseApi = useApplicationStore(({ enterpriseApi }) => {
    return enterpriseApi;
  });

  return useQuery({
    enabled: isDefined(mailingId),
    initialData: { success: false } as OperationResultWithData<Record<string, TwentileCountsByMailing>>,
    initialDataUpdatedAt: 0,
    queryFn: async () => {
      try {
        return await getTwentileCountsByMailing(enterpriseApi, mailingId);
      } catch (e) {
        const error = ensureError(e);

        console.error(error);

        return { success: false } as OperationResultWithData<Record<string, TwentileCountsByMailing>>;
      }
    },
    queryKey: nthQueryKeys.twentileCounts(mailingId ?? 0),
    retry: true,
    select: (result): NthCalculatorRow[] => {
      if (!result.success) {
        return [];
      }

      const data = result.data;

      if (isNotDefined(data)) {
        return [];
      }

      return Object.entries(data).map(
        ([mailCode, { firstOutput, mailingListId, receivedQuantity, targetQuantity, twentiles }]) => {
          return {
            approach: 'not-set',
            finalPct: receivedQuantity !== 0 ? firstOutput / receivedQuantity : null,
            firstOutput,
            mailCode,
            mailingListId,
            newTarget: firstOutput,
            notes: '',
            nthValue: null,
            receivedQuantity,
            selectedTwentileValues: [],
            targetQuantity,
            twentiles,
          };
        }
      );
    },
  });
};
